import React, { useMemo, useEffect, useState, useRef } from 'react';
import { theme } from '../../../theme';
import { GridBooking, GridVenue, DAY_OF_WEEK_SHORT, BOOKING_COLORS } from '../../../types/venue';
import BookingGridCell from './BookingGridCell';

interface BookingGridProps {
  venues: GridVenue[];
  bookings: GridBooking[];
  date: string;
  dateRange: { start: string; end: string };
  view: 'daily' | 'weekly';
  selectedVenueId?: string;
  startHour?: number;
  endHour?: number;
  slotDuration?: number; // in minutes
  onSlotClick: (venueId: string, date: string, time: string, cellRect: DOMRect) => void;
  onBookingClick: (booking: GridBooking) => void;
}

const BookingGrid: React.FC<BookingGridProps> = ({
  venues,
  bookings,
  date,
  dateRange,
  view,
  selectedVenueId,
  startHour = 7,
  endHour = 24,
  slotDuration = 60,
  onSlotClick,
  onBookingClick,
}) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Generate time slots
  const timeSlots = useMemo(() => {
    const slots: string[] = [];
    for (let hour = startHour; hour < endHour; hour++) {
      for (let min = 0; min < 60; min += slotDuration) {
        slots.push(`${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`);
      }
    }
    return slots;
  }, [startHour, endHour, slotDuration]);

  // Generate dates for weekly view
  const weekDates = useMemo(() => {
    if (view !== 'weekly') return [date];

    const dates: string[] = [];
    const startDate = new Date(dateRange.start + 'T00:00:00');

    for (let i = 0; i < 7; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      dates.push(d.toISOString().split('T')[0]);
    }
    return dates;
  }, [view, dateRange.start, date]);

  // Filter venues if one is selected
  const displayVenues = useMemo(() => {
    if (selectedVenueId) {
      return venues.filter((v) => v.id === selectedVenueId);
    }
    return venues;
  }, [venues, selectedVenueId]);

  // Get columns based on view
  const columns = useMemo(() => {
    if (view === 'weekly') {
      // In weekly view with one venue, columns are days
      return weekDates.map((d) => {
        const dateObj = new Date(d + 'T00:00:00');
        return {
          id: d,
          label: DAY_OF_WEEK_SHORT[dateObj.getDay()],
          subLabel: dateObj.getDate().toString(),
        };
      });
    } else {
      // In daily view, columns are venues
      return displayVenues.map((v) => ({
        id: v.id,
        label: v.name,
        subLabel: v.venueType?.replace('FUTBOL_', 'F') ?? '',
      }));
    }
  }, [view, displayVenues, weekDates]);

  // Get booking at a specific slot
  const getBookingAtSlot = (venueId: string, bookingDate: string, time: string): GridBooking | undefined => {
    return bookings.find(
      (b) =>
        b.venueId === venueId &&
        b.bookingDate === bookingDate &&
        b.startTime <= time &&
        b.endTime > time &&
        b.status !== 'CANCELLED'
    );
  };

  // Check if a time slot is in the past
  const isSlotPast = (bookingDate: string, time: string): boolean => {
    const now = currentTime;
    const slotDate = new Date(`${bookingDate}T${time}:00`);
    return slotDate < now;
  };

  // Check if current time indicator should show
  const isCurrentTimeSlot = (time: string): boolean => {
    if (view === 'weekly') return false;
    const today = new Date().toISOString().split('T')[0];
    if (date !== today) return false;

    const [slotHour, slotMin] = time.split(':').map(Number);
    const slotMinutes = slotHour * 60 + slotMin;
    const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();

    return currentMinutes >= slotMinutes && currentMinutes < slotMinutes + slotDuration;
  };

  // Calculate now indicator position
  const nowIndicatorPosition = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    if (view === 'weekly' || date !== today) return null;

    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();

    if (currentHour < startHour || currentHour >= endHour) return null;

    const minutesSinceStart = (currentHour - startHour) * 60 + currentMinute;
    const totalMinutes = (endHour - startHour) * 60;
    const slotHeight = 48;
    const totalSlots = totalMinutes / slotDuration;

    return (minutesSinceStart / slotDuration) * slotHeight;
  }, [currentTime, view, date, startHour, endHour, slotDuration]);

  const containerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `1px solid ${theme.colors.border}`,
    overflow: 'hidden',
  };

  const gridContainerStyle: React.CSSProperties = {
    display: 'flex',
    position: 'relative',
    maxHeight: 'calc(100vh - 280px)',
    overflowY: 'auto',
  };

  const timeColumnStyle: React.CSSProperties = {
    minWidth: '60px',
    width: '60px',
    backgroundColor: theme.colors.background,
    borderRight: `1px solid ${theme.colors.border}`,
    flexShrink: 0,
    position: 'sticky',
    left: 0,
    zIndex: 2,
  };

  const timeSlotStyle: React.CSSProperties = {
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    color: theme.colors.textSecondary,
    borderBottom: `1px solid ${theme.colors.border}`,
    fontWeight: theme.fonts.weightBold,
  };

  const gridBodyStyle: React.CSSProperties = {
    display: 'flex',
    flex: 1,
  };

  const columnStyle: React.CSSProperties = {
    flex: 1,
    minWidth: view === 'weekly' ? '80px' : '120px',
    position: 'relative',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    backgroundColor: theme.colors.background,
    borderBottom: `2px solid ${theme.colors.primary}`,
    position: 'sticky',
    top: 0,
    zIndex: 3,
  };

  const headerCellStyle: React.CSSProperties = {
    flex: 1,
    minWidth: view === 'weekly' ? '80px' : '120px',
    padding: '12px 8px',
    textAlign: 'center',
    borderRight: `1px solid ${theme.colors.border}`,
  };

  const headerLabelStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '2px',
  };

  const headerSubLabelStyle: React.CSSProperties = {
    fontSize: '11px',
    color: theme.colors.textSecondary,
  };

  const timeColumnHeaderStyle: React.CSSProperties = {
    minWidth: '60px',
    width: '60px',
    padding: '12px 8px',
    textAlign: 'center',
    backgroundColor: theme.colors.background,
    borderRight: `1px solid ${theme.colors.border}`,
    position: 'sticky',
    left: 0,
    zIndex: 4,
  };

  const nowIndicatorStyle: React.CSSProperties = {
    position: 'absolute',
    left: '60px',
    right: 0,
    height: '2px',
    backgroundColor: theme.colors.danger,
    zIndex: 10,
    pointerEvents: 'none',
  };

  const legendStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    padding: '12px',
    borderTop: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
  };

  const legendItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: theme.colors.textSecondary,
  };

  const legendColorStyle = (color: string): React.CSSProperties => ({
    width: '16px',
    height: '16px',
    borderRadius: '4px',
    backgroundColor: color,
  });

  if (displayVenues.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={{ padding: '40px', textAlign: 'center', color: theme.colors.textSecondary }}>
          No tienes canchas configuradas todavía
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={timeColumnHeaderStyle}>
          <span style={{ fontSize: '11px', color: theme.colors.textSecondary }}>Hora</span>
        </div>
        {columns.map((col) => (
          <div key={col.id} style={headerCellStyle}>
            <div style={headerLabelStyle}>{col.label}</div>
            <div style={headerSubLabelStyle}>{col.subLabel}</div>
          </div>
        ))}
      </div>

      {/* Grid */}
      <div style={gridContainerStyle} ref={gridRef}>
        {/* Time column */}
        <div style={timeColumnStyle}>
          {timeSlots.map((time) => (
            <div key={time} style={timeSlotStyle}>
              {time}
            </div>
          ))}
        </div>

        {/* Data columns */}
        <div style={gridBodyStyle}>
          {columns.map((col) => (
            <div key={col.id} style={columnStyle}>
              {timeSlots.map((time) => {
                // Determine venueId and bookingDate based on view
                const venueId = view === 'weekly' ? displayVenues[0]?.id : col.id;
                const bookingDate = view === 'weekly' ? col.id : date;

                if (!venueId) return null;

                const booking = getBookingAtSlot(venueId, bookingDate, time);
                const isPast = isSlotPast(bookingDate, time);
                const isCurrentTime = isCurrentTimeSlot(time);

                return (
                  <BookingGridCell
                    key={`${col.id}-${time}`}
                    booking={booking}
                    isAvailable={!booking && !isPast}
                    isPast={isPast}
                    isCurrentTime={isCurrentTime}
                    onClick={(e) => {
                      if (booking) {
                        onBookingClick(booking);
                      } else if (!isPast) {
                        const cellRect = e.currentTarget.getBoundingClientRect();
                        onSlotClick(venueId, bookingDate, time, cellRect);
                      }
                    }}
                  />
                );
              })}
            </div>
          ))}
        </div>

        {/* Now indicator */}
        {nowIndicatorPosition !== null && (
          <div
            style={{
              ...nowIndicatorStyle,
              top: `${nowIndicatorPosition}px`,
            }}
          />
        )}
      </div>

      {/* Legend */}
      <div style={legendStyle}>
        <div style={legendItemStyle}>
          <div style={legendColorStyle(BOOKING_COLORS.ONLINE)} />
          <span>Online</span>
        </div>
        <div style={legendItemStyle}>
          <div style={legendColorStyle(BOOKING_COLORS.FIXED)} />
          <span>Turno Fijo</span>
        </div>
        <div style={legendItemStyle}>
          <div style={legendColorStyle(BOOKING_COLORS.MANUAL)} />
          <span>Manual</span>
        </div>
        <div style={legendItemStyle}>
          <div style={legendColorStyle(BOOKING_COLORS.CANCELLED)} />
          <span>Cancelado</span>
        </div>
      </div>
    </div>
  );
};

export default BookingGrid;
